import { useState, useEffect } from 'react';
import { FaFlag, FaHeart, FaRegHeart } from 'react-icons/fa';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { HiUserGroup, HiDotsVertical } from 'react-icons/hi';
import { MdOutlineEdit } from 'react-icons/md';

export default function ChatMessage({
    processedMessages,
    handleSelectVideo,
    handleGroupClick,
    navigate,
    renderReadReceipt,
    handleReaction,
    hoveredMessageId,
    handleMessageMouseEnter,
    handleMessageMouseLeave,
    handleReportMessage,
    handleDeleteMessage,
    editingMessageId,
    handleEditMessage,
}) {
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownDirection, setDropdownDirection] = useState('up');

    useEffect(() => {
        if (!openDropdownId) return;
        const close = () => setOpenDropdownId(null);
        document.addEventListener('click', close);
        return () => document.removeEventListener('click', close);
    }, [openDropdownId]);

    const handleOpenDropdown = (e, messageId) => {
        if (openDropdownId === messageId) {
            setOpenDropdownId(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownDirection(rect.top < 180 ? 'down' : 'up');
        setOpenDropdownId(messageId);
    };

    return (
        <>
            {(processedMessages?.map((data, idx) => {
                return (
                    <div key={data.id || idx}>
                        {/* Date Header */}
                        {data.showDateHeader && (
                            <div className="flex justify-center my-4">
                                <p className="text-[10px] text-gray-600 dark:text-gray-400 font-medium">
                                    {data.dateHeader}
                                </p>
                            </div>
                        )}

                        {/* Message Content */}
                        {(() => {
                            // If shared_post exists
                            if (data.shared_post) {
                                const post = data.shared_post;
                                const creator = post.created_by;
                                return (
                                    <div
                                        className="mb-2"
                                        onMouseEnter={() => handleMessageMouseEnter(data.id)}
                                        onMouseLeave={handleMessageMouseLeave}
                                    >
                                        <div className={`flex flex-col ${data.is_sent_by_current_user ? 'items-end' : 'items-start'}`}>
                                            <div className="relative">
                                                {data.is_sent_by_current_user && (hoveredMessageId === data.id || openDropdownId === data.id) && (
                                                    <div
                                                        className="absolute top-0 -left-8 z-10"
                                                        onClick={e => e.stopPropagation()}
                                                    >
                                                        <button
                                                            onClick={(e) => handleOpenDropdown(e, data.id)}
                                                            className="bg-gray-100 dark:bg-[#333333] p-1.5 rounded-full shadow-md cursor-pointer hover:bg-gray-200 dark:hover:bg-[#444444]"
                                                        >
                                                            <HiDotsVertical className="text-[10px] text-gray-500 dark:text-white" />
                                                        </button>
                                                        {openDropdownId === data.id && (
                                                            <div className={`absolute right-0 ${dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white dark:bg-[#1E1D21] rounded-xl shadow-2xl py-1 z-50 w-[118px] border border-gray-100 dark:border-gray-800 overflow-hidden`}>
                                                                <button
                                                                    onClick={() => {
                                                                        handleEditMessage(data.id, data.message);
                                                                        setOpenDropdownId(null);
                                                                    }}
                                                                    className="flex items-center gap-2.5 w-full px-3.5 py-2 text-[12px] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
                                                                >
                                                                    <MdOutlineEdit className="text-[14px] text-gray-500 dark:text-gray-400" />
                                                                    Edit
                                                                </button>
                                                                <div className="h-px bg-gray-100 dark:bg-gray-800 mx-2.5" />
                                                                <button
                                                                    onClick={() => {
                                                                        handleDeleteMessage(data.id);
                                                                        setOpenDropdownId(null);
                                                                    }}
                                                                    className="flex items-center gap-2.5 w-full px-3.5 py-2 text-[12px] text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-[#2a1a1a] transition-colors"
                                                                >
                                                                    <RiDeleteBin6Line className="text-[14px]" />
                                                                    Delete
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {post.video_url && (
                                                    <video
                                                        src={post.video_url}
                                                        controls
                                                        disablePictureInPicture
                                                        onClick={() => handleSelectVideo(data.id, post.id)}
                                                        className="h-[12rem] w-[7.5rem] rounded mb-1"
                                                    />
                                                )}
                                                {creator && (
                                                    <div className="absolute top-1 left-1 flex items-center gap-x-1 bg-black bg-opacity-50 w-fit rounded-full px-1 py-0.5">
                                                        {creator.avatar_url && (
                                                            <img
                                                                src={creator.avatar_url}
                                                                alt="creator-avatar"
                                                                className="h-4 w-4 rounded-full object-cover border border-gray-400 dark:border-gray-800"
                                                            />
                                                        )}
                                                        <span className="text-[11px] line-clamp-1 font-extralight text-gray-300 dark:text-white">
                                                            {creator.full_name}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            {data.message && (
                                                <div className="px-2 py-1.5 bg-gradient-to-r from-[#02488C] dark:from-[#333333] to-[#3F196F] dark:to-[#333333] rounded-lg w-fit max-w-[70%]">
                                                    <p className="text-[13px] text-white dark:text-white leading-[15px] break-words select-none">
                                                        {data.message}
                                                    </p>
                                                </div>
                                            )}
                                            {data.is_sent_by_current_user && renderReadReceipt(data.id)}
                                        </div>
                                    </div>
                                );
                            }

                            // If shared_group exists
                            if (data.shared_group) {
                                const group = data.shared_group;
                                return (
                                    <div className="mb-2">
                                        <div className={`flex flex-col ${data.is_sent_by_current_user ? 'items-end' : 'items-start'}`}>
                                            <div
                                                onClick={() => handleGroupClick(group)}
                                                className="relative max-w-[70%] min-w-[200px]"
                                            >
                                                {group.group_profile_picture && (
                                                    <img
                                                        src={group.group_profile_picture}
                                                        alt="group-banner"
                                                        className="w-full h-20 object-cover rounded-t-lg"
                                                    />
                                                )}
                                                <div className="flex items-center gap-x-2 bg-gradient-to-r from-[#02488C] dark:from-[#333333] to-[#3F196F] dark:to-[#333333] rounded-b-lg p-2">
                                                    <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-full">
                                                        <HiUserGroup className="text-white text-sm" />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="text-white text-sm font-medium line-clamp-1">
                                                            {group.group_name}
                                                        </p>
                                                        <p className="text-gray-200 text-xs">
                                                            Group
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            {data.message && (
                                                <div className="px-2 py-1.5 bg-gradient-to-r from-[#02488C] dark:from-[#333333] to-[#3F196F] dark:to-[#333333] rounded-lg w-fit max-w-[70%] mt-1">
                                                    <p className="text-[13px] text-white dark:text-white leading-[15px] break-words select-none">
                                                        {data.message}
                                                    </p>
                                                </div>
                                            )}
                                            {data.is_sent_by_current_user && renderReadReceipt(data.id)}
                                        </div>
                                    </div>
                                );
                            }

                            // If shared_user exists
                            if (data.shared_user) {
                                const user = data.shared_user;
                                return (
                                    <div className="mb-2">
                                        <div className={`flex flex-col ${data.is_sent_by_current_user ? 'items-end' : 'items-start'}`}>
                                            <div
                                                onClick={() => navigate(`/account/${user.id}`)}
                                                className="relative max-w-[70%] min-w-[200px] cursor-pointer"
                                            >
                                                <div className="flex items-center gap-x-3 bg-gradient-to-r from-[#02488C] dark:from-[#333333] to-[#3F196F] dark:to-[#333333] rounded-lg p-3 border border-gray-300 dark:border-gray-800">
                                                    {user.avatar_url && (
                                                        <img
                                                            src={user.avatar_url}
                                                            alt="shared-user-avatar"
                                                            className="h-12 w-12 rounded-full object-cover border border-gray-400 dark:border-gray-800"
                                                        />
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="text-black dark:text-white text-sm font-medium line-clamp-1">
                                                            {user.full_name}
                                                        </p>
                                                        <p className="text-gray-600 dark:text-gray-400 text-xs line-clamp-1">
                                                            {user.username}
                                                        </p>
                                                        <div className="flex items-center gap-x-3 mt-1">
                                                            <span className="text-gray-500 dark:text-gray-500 text-xs line-clamp-1">
                                                                {user.followers_count} followers
                                                            </span>
                                                            <span className="text-gray-500 dark:text-gray-500 text-xs line-clamp-1">
                                                                {user.following_count} following
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {data.message && (
                                                <div className="px-2 py-1.5 bg-gradient-to-r from-[#02488C] dark:from-[#333333] to-[#3F196F] dark:to-[#333333] rounded-lg w-fit max-w-[70%] mt-1">
                                                    <p className="text-[13px] text-white dark:text-white leading-[15px] break-words select-none">
                                                        {data.message}
                                                    </p>
                                                </div>
                                            )}
                                            {data.is_sent_by_current_user && renderReadReceipt(data.id)}
                                        </div>
                                    </div>
                                );
                            }

                            // Regular message
                            return (
                                <div>
                                    {/* Sender Message (current user) */}
                                    {data.is_sent_by_current_user ? (
                                        <div className="flex flex-col items-end mb-1">
                                            {data.message && (
                                                <div
                                                    onDoubleClick={() => handleReaction(data.id)}
                                                    onMouseEnter={() => handleMessageMouseEnter(data.id)}
                                                    onMouseLeave={handleMessageMouseLeave}
                                                    className={`group px-2 py-1.5 bg-gradient-to-r from-[#02488C] dark:from-[#333333] to-[#3F196F] dark:to-[#333333] rounded-lg w-fit max-w-[70%] relative ${editingMessageId === data.id ? 'ring-2 ring-[#BDE6FF]' : ''}`}
                                                >
                                                    <p className="text-[13px] text-white dark:text-white leading-[15px] break-words select-none">
                                                        {data.message}
                                                    </p>
                                                    {data.is_edited && (
                                                        <span className="text-[10px] text-gray-300 italic">edited</span>
                                                    )}
                                                    {/* Three-dot button — absolutely positioned left of bubble */}
                                                    {(hoveredMessageId === data.id || openDropdownId === data.id) && (
                                                        <>
                                                            <div
                                                                className="absolute top-0 -left-8"
                                                                onClick={e => e.stopPropagation()}
                                                            >
                                                                <button
                                                                    onClick={(e) => handleOpenDropdown(e, data.id)}
                                                                    className="bg-gray-100 dark:bg-[#333333] p-1.5 rounded-full shadow-md cursor-pointer hover:bg-gray-200 dark:hover:bg-[#444444]"
                                                                >
                                                                    <HiDotsVertical className="text-[10px] text-gray-500 dark:text-white" />
                                                                </button>
                                                                {openDropdownId === data.id && (
                                                                    <div className={`absolute right-0 ${dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white dark:bg-[#1E1D21] rounded-xl shadow-2xl py-1 z-50 w-[118px] border border-gray-100 dark:border-gray-800 overflow-hidden`}>
                                                                        <button
                                                                            onClick={() => {
                                                                                handleEditMessage(data.id, data.message);
                                                                                setOpenDropdownId(null);
                                                                            }}
                                                                            className="flex items-center gap-2.5 w-full px-3.5 py-2 text-[12px] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors"
                                                                        >
                                                                            <MdOutlineEdit className="text-[14px] text-gray-500 dark:text-gray-400" />
                                                                            Edit
                                                                        </button>
                                                                        <div className="h-px bg-gray-100 dark:bg-gray-800 mx-2.5" />
                                                                        <button
                                                                            onClick={() => {
                                                                                handleDeleteMessage(data.id);
                                                                                setOpenDropdownId(null);
                                                                            }}
                                                                            className="flex items-center gap-2.5 w-full px-3.5 py-2 text-[12px] text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-[#2a1a1a] transition-colors"
                                                                        >
                                                                            <RiDeleteBin6Line className="text-[14px]" />
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button
                                                                title="React to message"
                                                                onClick={(e) => { e.stopPropagation(); handleReaction(data.id); }}
                                                                className="absolute top-0 -right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 dark:bg-[#333333] p-1.5 rounded-full shadow-md cursor-pointer hover:bg-gray-200 dark:hover:bg-[#444444] z-10"
                                                            >
                                                                {data.is_reacted
                                                                    ? <FaHeart className="text-[10px] text-red-500" />
                                                                    : <FaRegHeart className="text-[10px] text-gray-500 dark:text-white" />
                                                                }
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {data.reaction_count > 0 && (
                                                <button
                                                    onClick={() => handleReaction(data.id)}
                                                    className="flex items-center gap-0.5 bg-white dark:bg-[#1E1D21] border border-gray-200 dark:border-gray-700 rounded-full px-1.5 py-0.5 -mt-1.5 mr-1 shadow-sm hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors z-10"
                                                >
                                                    <FaHeart className="text-[9px] text-red-500" />
                                                    <span className="text-[10px] text-gray-600 dark:text-gray-400 leading-none">{data.reaction_count}</span>
                                                </button>
                                            )}
                                            {renderReadReceipt(data.id)}
                                        </div>
                                    ) : (
                                        // Receiver Message (other user) — original design with flag button
                                        <div className="flex flex-col items-start mb-1 w-full">
                                            {data.message && (
                                                <>
                                                    <div
                                                        onDoubleClick={() => handleReaction(data.id)}
                                                        onMouseEnter={() => handleMessageMouseEnter(data.id)}
                                                        onMouseLeave={handleMessageMouseLeave}
                                                        className="group px-2 text-wrap py-1.5 bg-gradient-to-r from-[#02488C] dark:from-[#333333] to-[#3F196F] dark:to-[#333333] rounded-lg w-fit max-w-[70%] relative"
                                                    >
                                                        <p className="text-[13px] text-white dark:text-white leading-[15px] break-words select-none">
                                                            {data.message}
                                                        </p>
                                                        {data.is_edited && (
                                                            <span className="text-[10px] text-gray-300 italic">edited</span>
                                                        )}
                                                        {hoveredMessageId === data.id && (
                                                            <>
                                                                <button
                                                                    title="React to message"
                                                                    onClick={(e) => { e.stopPropagation(); handleReaction(data.id); }}
                                                                    className="absolute top-0 -left-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 dark:bg-[#333333] p-1.5 rounded-full shadow-md cursor-pointer hover:bg-gray-200 dark:hover:bg-[#444444] z-10"
                                                                >
                                                                    {data.is_reacted
                                                                        ? <FaHeart className="text-[10px] text-red-500" />
                                                                        : <FaRegHeart className="text-[10px] text-gray-500 dark:text-white" />
                                                                    }
                                                                </button>
                                                                <button
                                                                    title="Report message"
                                                                    onClick={() => handleReportMessage(data.id)}
                                                                    className="absolute top-0 -right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-100 dark:bg-[#333333] p-1.5 rounded-full shadow-md cursor-pointer hover:bg-gray-200 dark:hover:bg-[#444444] z-10"
                                                                >
                                                                    <FaFlag className="text-[10px] text-gray-500 dark:text-white hover:text-red-500" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                    {data.reaction_count > 0 && (
                                                        <button
                                                            onClick={() => handleReaction(data.id)}
                                                            className="flex items-center gap-0.5 bg-white dark:bg-[#1E1D21] border border-gray-200 dark:border-gray-700 rounded-full px-1.5 py-0.5 -mt-1.5 ml-1 shadow-sm hover:bg-gray-50 dark:hover:bg-[#2a2a2a] transition-colors z-10"
                                                        >
                                                            <FaHeart className="text-[9px] text-red-500" />
                                                            <span className="text-[10px] text-gray-600 dark:text-gray-400 leading-none">{data.reaction_count}</span>
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                );
            }))}
        </>
    )
}
